import { useMemo } from 'react';
import useSWR from 'swr';

import { fetcher } from 'services/fetcher';
import { ApiStockType } from 'settings/productV2';
import { ENV_API_URL_CATALOG } from 'settings/env';
import {
  StocksReference,
  StockStatusType,
  StockType,
} from 'types/ProductPageV2/ProductsStocks';

export function useProductStocks(
  codeCouleur: string
): {
  stocks?: StockType[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  isLoaded: boolean;
  getStockStatusBySKU: (
    sku: string,
    stockData: StockType[]
  ) => StockStatusType | undefined;
} {
  let isLoaded = false;
  const { data: stockData, error } = useSWR<StocksReference>(
    `${ENV_API_URL_CATALOG}/stock/reference/${codeCouleur}/${ApiStockType.WEB}`,
    fetcher,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshInterval: 0,
    }
  );

  if (stockData || error) {
    isLoaded = true;
  }

  const stocks = useMemo(() => {
    if (!stockData || !stockData.VARIANTE) return;

    const variantes = stockData.VARIANTE;

    return Array.isArray(variantes)
      ? variantes.map(variante => {
          return {
            sku: variante.REFREEL,
            state: Number(variante.ETAT_STOCK_ID) as StockStatusType,
            size: variante.TAILLE,
            stateLabel: variante.ETAT_STOCK,
          };
        })
      : [
          {
            sku: variantes.REFREEL,
            state: Number(variantes.ETAT_STOCK_ID) as StockStatusType,
            size: variantes.TAILLE,
            stateLabel: variantes.ETAT_STOCK,
          },
        ];
  }, [stockData]);

  const getStockStatusBySKU = (
    sku: string,
    stockData: StockType[]
  ): StockStatusType | undefined => {
    const stock = stockData.find(stock => stock.sku === sku);

    return stock ? stock.state : undefined;
  };

  return {
    stocks,
    error,
    isLoaded,
    getStockStatusBySKU,
  };
}
